import 'intersection-observer'
import cn from 'clsx'

import React, { useEffect, useRef, useState } from 'react'
import styles from './index.module.css'

const OBSERVER_OPTIONS = {
  root: null,
}
type Props = {
  children: React.ReactNode
  className?: string
  hideLoadingLabel?: boolean
  loadingNode?: React.ReactNode
  loadNext: (next: string) => void
  next: string | null | undefined
}

/**
 * An infinite scroll component that automatically loads more content when the user scrolls to the bottom.
 * Uses Intersection Observer API to detect when the loading trigger element comes into view.
 *
 * @param children - The content to display that will be scrolled through
 * @param className - Optional CSS class name for the loading indicator
 * @param hideLoadingLabel - Whether to hide the loading label when loading (default: false)
 * @param loadingNode - The content to display while loading (default: 'Loading...')
 * @param loadNext - Callback function to load the next batch of content
 * @param next - The next page token/URL or null if no more content to load
 * @returns The infinite scroll component with content and loading indicator
 */
const InfiniteScroll = ({
  children,
  className,
  hideLoadingLabel,
  loadingNode = 'Loading...',
  loadNext,
  next,
}: Props): React.ReactNode => {
  const loader = useRef(null)
  const [loading, setLoading] = useState(false)

  const callback = entries => {
    const target = entries[0]

    if (target.isIntersecting) {
      setLoading(true)
    }
  }

  const observer = new IntersectionObserver(callback, OBSERVER_OPTIONS)
  useEffect(() => {
    if (loader && loader.current) {
      observer.observe(loader.current)
    }

    return () => {
      if (loader && loader.current) {
        observer.unobserve(loader.current)
      }
    }
  }, [loader, next])
  useEffect(() => {
    setLoading(false)
  }, [next])
  useEffect(() => {
    if (loading && next) {
      loadNext(next)
    }
  }, [loading])
  return (
    <>
      {children}
      {next && (
        <div
          className={cn(styles.infiniteScrollLoader, className)}
          ref={loader}
        >
          {loading && !hideLoadingLabel && loadingNode}
        </div>
      )}
    </>
  )
}

export default InfiniteScroll
